import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentBaseTree = ({ 
  documentBases, 
  folders,
  selectedItem, 
  onItemSelect, 
  onCreateFolder, 
  onDeleteItem, 
  onRenameItem,
  onToggleVisibility 
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [treeData, setTreeData] = useState([]);

  // Build hierarchical tree structure from document bases and folders
  useEffect(() => {
    if (!documentBases) {
      setTreeData([]);
      return;
    }

    const buildTreeData = () => {
      // Create a map of folders by document base ID
      const foldersByBase = {};
      if (folders) {
        folders?.forEach(folder => {
          if (!foldersByBase?.[folder?.documentBaseId]) {
            foldersByBase[folder.documentBaseId] = [];
          }
          foldersByBase?.[folder?.documentBaseId]?.push(folder);
        });
      }

      // Build tree with folders as children of document bases
      const tree = documentBases?.map(base => {
        const baseFolders = foldersByBase?.[base?.id] || [];
        
        // Build folder hierarchy within each document base
        const folderMap = new Map();
        const rootFolders = [];

        // First pass: create folder objects and identify root folders
        baseFolders?.forEach(folder => {
          const folderNode = {
            ...folder,
            type: 'folder',
            children: [],
            documentCount: 0 // This could be enhanced to show actual document count
          };
          folderMap?.set(folder?.id, folderNode);
          
          if (!folder?.parentFolderId) {
            rootFolders?.push(folderNode);
          }
        });

        // Second pass: build parent-child relationships
        baseFolders?.forEach(folder => {
          if (folder?.parentFolderId && folderMap?.has(folder?.parentFolderId)) {
            const parentFolder = folderMap?.get(folder?.parentFolderId);
            const childFolder = folderMap?.get(folder?.id);
            parentFolder?.children?.push(childFolder);
          }
        });

        return {
          ...base,
          type: 'base',
          children: rootFolders,
          documentCount: base?.documentCount || 0
        };
      });

      return tree;
    };

    setTreeData(buildTreeData());
    
    // Auto-expand first document base to show folders
    if (documentBases?.length > 0) {
      setExpandedItems(prev => new Set([...prev, documentBases[0].id]));
    }
  }, [documentBases, folders]);

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(itemId)) {
      newExpanded?.delete(itemId);
    } else {
      newExpanded?.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleContextMenu = (e, item) => {
    e?.preventDefault();
    e?.stopPropagation();
    setContextMenu({
      x: e?.clientX,
      y: e?.clientY,
      item: item
    });
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e?.preventDefault();
    if (draggedItem && draggedItem?.id !== targetItem?.id) {
      // Handle reordering logic here
      console.log('Moving', draggedItem?.name, 'to', targetItem?.name);
    }
    setDraggedItem(null);
  };

  const renderTreeItem = (item, level = 0) => {
    const isExpanded = expandedItems?.has(item?.id);
    const isSelected = selectedItem?.id === item?.id && selectedItem?.type === item?.type;
    const hasChildren = item?.children && item?.children?.length > 0;

    return (
      <div key={`${item?.type}-${item?.id}`} className="select-none">
        <div
          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted transition-colors ${
            isSelected ? 'bg-primary/10 text-primary' : 'text-foreground'
          }`}
          style={{ paddingLeft: `${8 + level * 16}px` }}
          onClick={() => onItemSelect(item)}
          onContextMenu={(e) => handleContextMenu(e, item)}
          draggable={item?.type !== 'base'}
          onDragStart={(e) => handleDragStart(e, item)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, item)}
        >
          {hasChildren && (
            <Button
              variant="ghost"
              size="icon"
              className="w-4 h-4 p-0 hover:bg-transparent"
              onClick={(e) => {
                e?.stopPropagation();
                toggleExpanded(item?.id);
              }}
            >
              <Icon 
                name="ChevronRight" 
                size={12} 
                className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </Button>
          )}
          
          {!hasChildren && <div className="w-4" />}
          
          <Icon 
            name={item?.type === 'base' ? 'Database' : 'Folder'} 
            size={16} 
            className={item?.type === 'base' ? 'text-primary' : 'text-muted-foreground'}
          />
          
          <span className="flex-1 text-sm truncate">{item?.name}</span>
          
          {item?.type === 'base' && (
            <Icon 
              name={item?.isPublic ? 'Globe' : 'Lock'} 
              size={12} 
              className="text-muted-foreground"
            />
          )}
          
          {item?.documentCount !== undefined && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {item?.documentCount}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {item?.children?.map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Document Bases</h2>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconSize={16}
            onClick={() => onCreateFolder('base')}
          >
            New Base
          </Button>
        </div>
        
        <div className="relative">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search document bases..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Tree Navigation */}
      <div className="flex-1 overflow-y-auto p-2">
        {treeData?.map(base => renderTreeItem(base))}
      </div>
      
      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="fixed bg-popover border border-border rounded-lg shadow-lg py-2 z-50 min-w-48"
            style={{ left: contextMenu?.x, top: contextMenu?.y }}
          >
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2"
              onClick={() => {
                onCreateFolder('folder', contextMenu?.item);
                setContextMenu(null);
              }}
            >
              <Icon name="FolderPlus" size={16} />
              New Folder
            </button>
            
            <div className="border-t border-border my-1" />
            
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2"
              onClick={() => {
                onRenameItem(contextMenu?.item);
                setContextMenu(null);
              }}
            >
              <Icon name="Edit2" size={16} />
              Rename
            </button>

            {contextMenu?.item?.type === 'base' && (
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2"
                onClick={() => {
                  onToggleVisibility(contextMenu?.item);
                  setContextMenu(null);
                }}
              >
                <Icon name={contextMenu?.item?.isPublic ? 'Lock' : 'Globe'} size={16} />
                Make {contextMenu?.item?.isPublic ? 'Private' : 'Public'}
              </button>
            )}

            <div className="border-t border-border my-1" />
            
            <button
              className="w-full px-4 py-2 text-left text-sm text-error hover:bg-muted transition-colors flex items-center gap-2"
              onClick={() => {
                onDeleteItem(contextMenu?.item);
                setContextMenu(null);
              }}
            >
              <Icon name="Trash2" size={16} />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentBaseTree;